// Washer.js - Máquina de lavar roupa LG, via ThinQ
//
// Vista de consulta: a LG só permite iniciar ciclos remotamente se o
// controlo remoto estiver ativado fisicamente na máquina, e o rodapé
// explica-o quando não está.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WashingMachine, Activity, AlertCircle } from 'lucide-react';
import ViewHeader from './ViewHeader';

const API_URL = '/api';

// Um ciclo de lavagem demora cerca de uma hora; ler de minuto a minuto não
// traria nada de novo.
const INTERVALO_MS = 60000;

// Estados em que a máquina está mesmo a trabalhar, para o cartão acender.
const A_TRABALHAR = ['RUNNING', 'RINSING', 'SPINNING'];

function Washer({ onBack }) {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/lgthinq/devices`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.detail || t('erros.buscarLgthinq'));
        return;
      }
      if (data.devices) setDevices(data.devices);
    } catch (err) {
      setError(t('erros.buscarLgthinq'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, INTERVALO_MS);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const maquina = devices[0];
  const estado = maquina?.state || {};
  const cicloAtual = estado.runState?.currentState;
  const controloRemoto = estado.remoteControlEnable?.remoteControlEnabled;
  const horas = estado.timer?.remainHour || 0;
  const minutos = estado.timer?.remainMinute || 0;

  // defaultValue deixa passar um estado que a LG venha a acrescentar, em vez
  // de o fazer desaparecer do ecrã.
  const nomeDoEstado = cicloAtual
    ? t(`roupa.estados.${cicloAtual}`, { defaultValue: cicloAtual })
    : '—';

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={WashingMachine}
          iconClass="icon-heatpump"
          titulo={t('roupa.titulo')}
          subtitulo={t('roupa.subtitulo')}
          onBack={onBack}
          onRefresh={fetchDevices}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!maquina ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('roupa.aCarregar')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${A_TRABALHAR.includes(cicloAtual) ? 'online' : ''}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className="status-dot online" />
                  <span>{maquina.model}</span>
                </div>
                <div className="power-badge on">{nomeDoEstado}</div>
              </div>
            </div>

            {(horas > 0 || minutos > 0) && (
              <div className="heatpump-info-card">
                <h3>{t('roupa.tempoRestante')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <Activity size={20} />
                    <div>
                      <span className="info-label">{t('roupa.falta')}</span>
                      <span className="info-value">{horas}h {minutos}m</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="heatpump-info-card">
              <h3>{t('comum.informacoes')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <Activity size={20} />
                  <div>
                    <span className="info-label">{t('roupa.ciclosTotais')}</span>
                    <span className="info-value">{estado.cycle?.cycleCount ?? '—'}</span>
                  </div>
                </div>
              </div>
              <p className="section-help" style={{ marginTop: '1rem' }}>
                {controloRemoto
                  ? t('roupa.controloRemotoAtivo')
                  : t('roupa.controloRemotoInativo')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Washer;
